import './KLCSVViewer.css'

import { Spin, Table } from 'antd'
import { Spacing } from 'common/stylings'
import Stack from 'components/Stack'
import Text from 'components/Typography'
import Papa from 'papaparse'
import React, { useEffect, useState } from 'react'

interface KLCSVViewerProps {
  url: string
  reportCSVURL?: string
  studyName: string
  onRadiologyReportTextChange?: (text: string) => void
}

interface CsvData {
  [key: string]: any
}

const KLIndivCSVViewer: React.FC<KLCSVViewerProps> = ({
  url,
  reportCSVURL,
  studyName,
  onRadiologyReportTextChange,
}) => {
  const [data, setData] = useState<CsvData[]>([])
  const [columns, setColumns] = useState<
    Array<{ title: string; dataIndex: string; key: string; width?: number }>
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [_, setRadiologyReportText] = useState<string>('')

  useEffect(() => {
    const fetchCSVData = async () => {
      try {
        setIsLoading(true)
        setError(false)
        const response = await fetch(url)

        if (response.status !== 200) {
          setError(true)
          setIsLoading(false)
          return
        }

        const csvData = await response.text()

        Papa.parse(csvData, {
          header: true,
          dynamicTyping: true,
          complete: async (result: any) => {
            const parsedData: CsvData[] = result.data

            // filter by Study ID first (for Segmed)
            let filteredData = parsedData.filter(
              row =>
                row['Study ID'] && row['Study ID'].trim() === studyName.trim(),
            )

            // if no data is found with Study ID, filter by Patient ID (for Gradient Health)
            if (filteredData.length === 0) {
              filteredData = parsedData.filter(
                row =>
                  row['PatientID'] &&
                  row['PatientID'].trim() === studyName.trim(),
              )

              if (filteredData.length > 0) {
                const patientIDCols = [
                  {
                    title: 'Patient ID',
                    dataIndex: 'PatientID',
                    key: 'Patient ID',
                    width: 85,
                  },
                  {
                    title: 'Report Text',
                    dataIndex: 'report_text',
                    key: 'report_text',
                    width: 530,
                  },
                ]
                setColumns(patientIDCols)

                const extractedText = filteredData
                  .map(
                    row =>
                      `Patient ID: ${row['PatientID']}\nReport: ${row['report_text']}`,
                  )
                  .join('')

                setRadiologyReportText(extractedText)
                onRadiologyReportTextChange &&
                  onRadiologyReportTextChange(extractedText)

                setData(filteredData)
              } else if (reportCSVURL && reportCSVURL !== url) {
                try {
                  const reportResponse = await fetch(reportCSVURL)
                  if (reportResponse.status === 200) {
                    const reportCSVData = await reportResponse.text()

                    Papa.parse(reportCSVData, {
                      header: true,
                      dynamicTyping: true,
                      complete: (reportResult: any) => {
                        const reportParsedData: CsvData[] = reportResult.data

                        const reportFilteredData = reportParsedData.filter(
                          row =>
                            row['study_instance_uid'] &&
                            row['study_instance_uid'].trim() ===
                              studyName.trim(),
                        )

                        if (reportFilteredData.length > 0) {
                          const studyInstanceUidCols = [
                            {
                              title: 'Study Instance UID',
                              dataIndex: 'study_instance_uid',
                              key: 'study_instance_uid',
                              width: 200,
                            },
                            {
                              title: 'Report Text',
                              dataIndex: 'report_text',
                              key: 'report_text',
                              width: 530,
                            },
                          ]
                          setColumns(studyInstanceUidCols)

                          const extractedText = reportFilteredData
                            .map(row => {
                              return `Study Instance UID: ${row['study_instance_uid']}\n\nReport:\n${row['report_text']}\n\n---------------------------\n`
                            })
                            .join('\n')

                          setRadiologyReportText(extractedText)
                          onRadiologyReportTextChange &&
                            onRadiologyReportTextChange(extractedText)

                          setData(reportFilteredData)
                        } else {
                          setData([])
                        }
                      },
                    })
                    setIsLoading(false)
                    return
                  }
                } catch (reportError) {
                  console.error('Error fetching report CSV:', reportError)
                }
                setData([])
              } else {
                setData([])
              }
            } else {
              const studyIDCols = [
                {
                  title: 'Study ID',
                  dataIndex: 'Study ID',
                  key: 'Study ID',
                  width: 85,
                },
                {
                  title: 'Report',
                  dataIndex: 'Report',
                  key: 'Report',
                  width: 530,
                },
              ]
              setColumns(studyIDCols)

              const extractedText = filteredData
                .map(row => {
                  const reportKey = row['PatientID']
                    ? `Patient ID: ${row['PatientID']}\n\n`
                    : `Study ID: ${row['Study ID']}\n\n`
                  return `${reportKey}Report:\n${row['Report']}\n\n---------------------------\n`
                })
                .join('\n')

              setRadiologyReportText(extractedText)
              onRadiologyReportTextChange &&
                onRadiologyReportTextChange(extractedText)

              setData(filteredData)
            }

            setIsLoading(false)
          },
        })
      } catch (error) {
        setError(true)
        setIsLoading(false)
      }
    }

    fetchCSVData()
  }, [url, reportCSVURL, studyName, onRadiologyReportTextChange])

  if (error) {
    return null
  }

  return (
    <Stack vertical gutter={Spacing.MEDIUM}>
      <Stack vertical>
        <div className="csv-viewer">
          <Text style={{ fontSize: '18px' }} fontSize={16} fontWeight="bold">
            {data.length > 0 && data[0]['study_instance_uid']
              ? `Study Instance UID: ${studyName}`
              : data.length > 0 && data[0]['Study ID']
              ? `Study ID: ${studyName}`
              : `Patient ID: ${studyName}`}
          </Text>
          {isLoading ? (
            <div className="csv-viewer-wrapper">
              <Spin tip="Loading CSV data..." />
            </div>
          ) : (
            <div>
              <Table
                className="csv-viewer"
                dataSource={data}
                columns={columns}
                pagination={false}
                rowKey={(record: any, index: any) =>
                  record['study_instance_uid'] ||
                  record['Study ID'] ||
                  record['PatientID'] ||
                  index.toString()
                }
              />
            </div>
          )}
        </div>
      </Stack>
    </Stack>
  )
}

export default KLIndivCSVViewer
